import cn from 'classnames'
import dynamic from 'next/dynamic'
import s from './Layout.module.scss'
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { useAuthUI } from '@corratech/pylot-auth-manager'
import { Navbar } from '@components/common'
import { useAcceptCookies } from '@corratech/pylot-utils'
import { Button } from '@corratech/pylot-ui'
import { LoadingIndicator } from '@components/LoadingIndicator'
import formStyles from '@commonStyles/Forms.module.scss'
import { useTranslation } from 'next-i18next'
import { AuthWrapper } from '@components/common/AuthWrapper'
import { PromoBanner } from '@corratech/pylot-promobanner'
import { Cross } from '@components/icons'
import { usePreloadedData } from '@preload'
import { useCmsBlocks } from '@pylot-data/hooks/cms/use-cms-blocks'
import { RichContent } from '@corratech/pylot-rich-content'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import { useRouter } from 'next/router'
import { validate as EmailValidate } from 'email-validator'
import Link from 'next/link'

const dynamicProps = {
    // eslint-disable-next-line react/display-name
    loading: () => <LoadingIndicator />
}
const LoginView = dynamic(
    () => import('@components/alfakher/pylot-auth/src/LoginView'),
    { ...dynamicProps }
)

const SignUpView = dynamic(
    () => import('@components/alfakher/pylot-auth/src/SignUpView'),
    { ...dynamicProps }
)
const ForgotPassword = dynamic(
    () => import('@components/alfakher/pylot-auth/src/ForgotPassword'),
    { ...dynamicProps }
)

const ResetPassword = dynamic(
    () => import('@components/alfakher/pylot-auth/src/ResetPassword'),
    { ...dynamicProps }
)

const FeatureBar = dynamic(() => import('@components/common/FeatureBar'), {
    ...dynamicProps
})

const Footer = dynamic(() => import('@components/common/Footer'), {
    ssr: false
})

const Sidebar = dynamic(() => import('@corratech/pylot-ui/src/Sidebar'))

const CartSidebarView = dynamic(
    () => import('@components/alfakher/pylot-cart/CartSidebarView')
)

const Modal = dynamic(() => import('@components/alfakher/pylot-ui/src/Modal'))

interface Props {
    authRequired: boolean
    pageProps: any
    setIsLoader?: Dispatch<SetStateAction<boolean>>
}

enum MODAL_VIEW_ENUMS {
    LOGIN_VIEW = 'LOGIN_VIEW',
    SIGNUP_VIEW = 'SIGNUP_VIEW',
    FORGOT_VIEW = 'FORGOT_VIEW',
    RESET_VIEW = 'PASSWORD_RESET_VIEW'
}

enum MODAL_HASH_ENUMS {
    LOGIN_VIEW = 'show-login-form',
    SIGNUP_VIEW = 'show-signup-form',
    FORGOT_VIEW = 'show-forgot-password',
    RESET_VIEW = 'show-reset-form',
    GUEST = 'session-expired'
}
const privacyLink = '/content/privacy-policy-cookie-restriction-mode'

const Layout: FC<Props> = ({ children, authRequired, setIsLoader }) => {
    const { displaySidebar, closeSidebar, displaySearch, openToast } = useUI()
    const {
        modalView,
        displayModal,
        closeAuthModal,
        setModalView,
        openAuthModal
    } = useAuthUI()
    const { acceptedCookies, onAcceptCookies } = useAcceptCookies()
    const { t } = useTranslation()
    const config = useStoreConfig()
    const { header: { variables } = {} } = config.preload
    const [isHashChanged, setIsHashChanged] = useState<boolean>(false)
    const initialData = usePreloadedData('header')

    const { items } = useCmsBlocks(variables?.identifiers ?? [], {
        initialData,
        revalidateOnMount: true,
        revalidateOnFocus: false
    })

    const router = useRouter()

    const onHashChangeComplete = (): void => {
        if (typeof window != 'undefined' && window.location.hash) {
            switch (window.location.hash.replace('#', '')) {
                case MODAL_HASH_ENUMS.LOGIN_VIEW:
                    setModalView(MODAL_VIEW_ENUMS.LOGIN_VIEW)
                    setIsHashChanged(true)
                    break
                case MODAL_HASH_ENUMS.SIGNUP_VIEW:
                    setModalView(MODAL_VIEW_ENUMS.SIGNUP_VIEW)
                    setIsHashChanged(true)
                    break
                case MODAL_HASH_ENUMS.FORGOT_VIEW:
                    setModalView(MODAL_VIEW_ENUMS.FORGOT_VIEW)
                    setIsHashChanged(true)
                    break
                case MODAL_HASH_ENUMS.RESET_VIEW:
                    {
                        const url = new URL(router.asPath, location.origin)
                        const email = url.searchParams.get('email')
                        const token = url.searchParams.get('token')

                        if (email && token && EmailValidate(email)) {
                            setModalView(MODAL_VIEW_ENUMS.RESET_VIEW, {
                                email: email,
                                token: token
                            })
                            setIsHashChanged(true)
                        }
                    }
                    break
                case MODAL_HASH_ENUMS.GUEST:
                    if (window?.localStorage.getItem('is_authorised') === '1') {
                        openToast(
                            t('Your session expired. Please sign in again.'),
                            ToastType.Warning
                        )
                        router.replace('/')
                    }
                    break
            }
        }
    }

    useEffect(() => {
        onHashChangeComplete()
        window.addEventListener('hashchange', onHashChangeComplete, false)
        return () => {
            window.removeEventListener(
                'hashchange',
                onHashChangeComplete,
                false
            )
        }
    }, [])

    useEffect(() => {
        if (isHashChanged === true) {
            openAuthModal()
            setIsHashChanged(false)
        }
    }, [isHashChanged])

    /**
     * Remove hash when popup is closed
     */
    const closeModal = () => {
        closeAuthModal()
        if (window.location.hash) {
            history.replaceState(null, '', ' ')
        }
    }

    return (
        <div className={cn(s.root, `store_${router.locale}`)} id="layout-main">
            <a className={s['skip-to-content']} href="#contentarea">
                {t('Skip to Content')}
            </a>
            {items && items.length > 0 && (
                <>
                    {items[1] && (
                        <div className={s['legal-container']}>
                            <RichContent html={items[1]?.content} />
                        </div>
                    )}
                    {items[0] && (
                        <PromoBanner
                            className={s['promo-banner']}
                            cookieName="promobanner"
                            closeButton={<Cross />}
                        >
                            <RichContent html={items[0]?.content} />
                        </PromoBanner>
                    )}
                </>
            )}
            <Navbar displaySearch={displaySearch} setIsLoader={setIsLoader} />
            <main className={s['main-container']} id="contentarea">
                <AuthWrapper authRequired={authRequired}>
                    {children}
                </AuthWrapper>
            </main>
            <Footer />
            {displayModal && (
                <Modal open={displayModal} onClose={closeModal}>
                    <div className={cn(formStyles.root)}>
                        {modalView === MODAL_VIEW_ENUMS.LOGIN_VIEW && (
                            <LoginView />
                        )}
                        {modalView === MODAL_VIEW_ENUMS.SIGNUP_VIEW && (
                            <SignUpView />
                        )}
                        {modalView === MODAL_VIEW_ENUMS.FORGOT_VIEW && (
                            <ForgotPassword />
                        )}

                        {modalView === MODAL_VIEW_ENUMS.RESET_VIEW && (
                            <ResetPassword />
                        )}
                    </div>
                </Modal>
            )}
            {displaySidebar && (
                <Sidebar open={displaySidebar} onClose={closeSidebar}>
                    <CartSidebarView />
                </Sidebar>
            )}
            <FeatureBar
                title={
                    <>
                        {t(
                            'This site uses cookies to improve your experience. By clicking, you agree to our'
                        )}{' '}
                        <Link href={privacyLink}>{t('Privacy Policy.')}</Link>
                    </>
                }
                hide={acceptedCookies}
                action={
                    <Button className="mx-5" onClick={() => onAcceptCookies()}>
                        {t('Accept cookies')}
                    </Button>
                }
            />
        </div>
    )
}

export default Layout
